import React, { useEffect, useState } from "react";
import { CloudUpload, X, AlertCircle, Download, FileX, Clock, ExternalLink } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import BackButton from "../reusableField/BackButton";
import { useMutation, useQuery } from "@apollo/client";
import { BULK_UPLOAD_LEADS, GET_BULK_UPLOAD_AUDIT_TRAIL } from "../services/userApi";
import { message } from "antd";

const AuditTrailModal = ({ isOpen, onClose, auditData, loading }) => {
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (item) => {
    if (item.errorExcelUrl) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          Has Errors
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
        Success
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[70vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-4 py-2.5 flex items-center justify-between bg-gradient-to-r from-[#ad0101] to-[#ad0101]">
          <div className="flex items-center gap-2">
            <Clock className="text-white" size={18} />
            <h3 className="text-white font-semibold text-base">Audit Trail</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors rounded-full hover:bg-white/10 p-1"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto max-h-[calc(70vh-50px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : !auditData || auditData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <Clock className="text-gray-300 mb-3" size={36} />
              <p className="text-gray-500 text-sm text-center">No upload history available</p>
            </div>
          ) : (
            <div className="p-4">
              {auditData.map((item, index) => (
                <div
                  key={item.id}
                  className={`border rounded-lg p-3 hover:shadow-md transition-shadow ${index !== auditData.length - 1 ? "mb-3" : ""
                    } ${item.errorExcelUrl ? "border-red-200 bg-red-50/30" : "border-gray-200 bg-white"}`}
                >
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Upload #{item.id}
                      </h4>
                      {getStatusBadge(item)}
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 mb-2">
                    Uploaded by <span className="font-medium text-gray-900">{item.uploadedBy}</span>
                  </p>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <div>
                      <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-0.5">Uploaded At</p>
                      <p className="text-xs text-gray-900 font-medium">
                        {formatDate(item.uploadedAt)}
                      </p>
                    </div>
                    {item.modifiedAt && (
                      <div>
                        <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-0.5">Modified At</p>
                        <p className="text-xs text-gray-900 font-medium">
                          {formatDate(item.modifiedAt)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2 border-t border-gray-200">
                    <a
                      href={item.excelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-600 rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                    >
                      <Download size={14} />
                      Input Sheet
                    </a>
                    {item.errorExcelUrl && (
                      <a
                        href={item.errorExcelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium"
                      >
                        <Download size={14} />
                        Error Report
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ErrorModal = ({ errorData, isSuccess, onClose, onTryAgain }) => {
  if (!errorData) return null;

  const stats = [
    { label: "Total Rows", value: errorData["total rows"] },
    { label: "Valid Rows", value: errorData.total_valid_rows },
    { label: "Error Rows", value: errorData.total_error_rows },
    { label: "Rows Uploaded", value: errorData.rows_uploaded },
    { label: "Total Errors", value: errorData.total_errors },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className={`px-2 py-2 flex items-center justify-between ${isSuccess ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-2">
            <FileX className="text-white" size={22} />
            <h3 className="text-white font-semibold text-lg">
              {isSuccess ? 'Upload Successful' : 'Upload Failed'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4">
          {/* Success message - only show if success */}
          {isSuccess && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <AlertCircle className="text-green-500 mt-0.5 shrink-0" size={18} />
              <p className="text-green-700 text-sm leading-relaxed">
                Your file has been uploaded successfully.
              </p>
            </div>
          )}

          {/* Error message - only show if not success */}
          {!isSuccess && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-l p-1 mb-2">
              <AlertCircle className="text-red-500 mt-0.5 shrink-0" size={18} />
              <p className="text-red-700 text-sm leading-relaxed">
                Your sheet has{" "}
                <span className="font-bold text-red-400">
                  {errorData.total_errors}
                </span>{" "}
                error{errorData.total_errors > 1 ? "s" : ""}. Please fix and
                upload the original sheet.
              </p>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {stats.map(({ label, value }) => (
              <div
                key={label}
                className={`rounded-md px-4 py-1 flex flex-col leading-tight border ${label === "Total Errors" || label === "Error Rows"
                  ? "bg-red-50 border-red-200"
                  : label === "Valid Rows"
                    ? "bg-green-50 border-green-200"
                    : "bg-gray-50 border-gray-200"
                  }`}
              >
                <span
                  className={`text-lg font-bold ${label === "Total Errors" || label === "Error Rows"
                    ? "text-red-600"
                    : label === "Valid Rows"
                      ? "text-green-600"
                      : "text-gray-700"
                    }`}
                >
                  {value}
                </span>
                <span className="text-xs text-gray-500 font-medium">{label}</span>
              </div>
            ))}
          </div>

          {/* Actions - only show if not success */}
          {!isSuccess && (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = errorData.error_report_url;
                  a.download = errorData.error_file_name;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-2.5 rounded-xl hover:bg-red-700 transition-colors font-medium text-sm"
              >
                <Download size={16} />
                Download Error Report
              </button>
              <button
                type="button"
                onClick={onTryAgain}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FileUpload = () => {
  const accountId = sessionStorage.getItem("accountId");
  const userId = sessionStorage.getItem("userId");
  const location = useLocation();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const fileInputRef = React.useRef(null);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [assignToUser, setAssignToUser] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formId = location.state?.formId;

  const [bulkUpload, { data: bulkUploadData, loading: isLoading }] =
    useMutation(BULK_UPLOAD_LEADS);

  const { data: auditTrailData, loading: auditLoading, refetch: refetchAuditTrail } = useQuery(
    GET_BULK_UPLOAD_AUDIT_TRAIL,
    {
      variables: {
        userId: accountId === 1 ? null : parseInt(userId)
      },
      fetchPolicy: "network-only"
    }
  );

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFileName(selectedFile.name);
      setFile(selectedFile);
    }
  };

  useEffect(() => {
    if (accountId !== 1) {
      setAssignToUser(true);
      console.log("account_id", accountId)
    }
  }, [accountId]);

  const handleBrowseClick = () => {
    document.querySelector(".upload-input").click();
  };

  const handleClearError = () => {
    setErrorData(null);
    setShowErrorModal(false);
    setFile(null);
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Redirect to leads page after successful upload with delay
    if (isSuccess) {
      setTimeout(() => {
        navigate("/leads");
      }, 1000); // 2 second delay before redirect
    } else {
      setIsSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!file) {
      messageApi.error("No file selected");
      return;
    }

    try {
      const result = await bulkUpload({
        variables: {
          formId: 22,
          document: file,
          assignToUser: assignToUser,
        },
      });

      if (result.data?.bulkUploadLeads) {
        const response = result.data.bulkUploadLeads;
        const uploadData = response.data;

        // Refetch audit trail after successful upload
        refetchAuditTrail();

        // Check if upload was successful
        if (response.success === true) {
          // Success case - show modal with stats but no error message/buttons
          setIsSuccess(true);
          setErrorData(uploadData);
          setShowErrorModal(true);
          messageApi.success("File uploaded successfully!");
          setFile(null);
          setFileName("");
          if (fileInputRef.current) fileInputRef.current.value = "";
        } else {
          // Error case - show modal with error message and buttons
          setIsSuccess(false);
          setErrorData(uploadData);
          setShowErrorModal(true);
          setFile(null);
          setFileName("");
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      }
    } catch (err) {
      messageApi.error(err.message || "An error occurred during upload");
      console.error("Upload error:", err);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCloseModal = () => {
    setShowErrorModal(false);

    // Redirect to leads page after successful upload with delay
    if (isSuccess) {
      setTimeout(() => {
        navigate("/leads");
      }, 1500); // 1.5 second delay before redirect
    }
  };

  const handleOpenAuditTrail = () => {
    refetchAuditTrail();
    setShowAuditModal(true);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white font-['Inter',sans-serif] relative">
      {contextHolder}

      {/* Error Modal */}
      {showErrorModal && (
        <ErrorModal
          errorData={errorData}
          isSuccess={isSuccess}
          onClose={handleCloseModal}
          onTryAgain={handleClearError}
        />
      )}

      {/* Audit Trail Modal */}
      <AuditTrailModal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        auditData={auditTrailData?.getBulkUploadAuditTrail}
        loading={auditLoading}
      />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          marginLeft: "100px",
          marginTop: "95px",
        }}
      >
        <BackButton label="Back" onClick={handleBack} />
      </div>

      {/* Audit Trail Button - positioned in top right */}
      <button
        type="button"
        onClick={handleOpenAuditTrail}
        className="absolute top-5 right-5 mr-[100px] mt-[95px] flex items-center gap-1.5 bg-[#ad0101] text-white border-none text-xs font-medium cursor-pointer px-3 py-2 rounded-lg transition-colors hover:bg-[#ad0101] shadow-md"
      >
        <Clock size={16} />
        Audit Trail
      </button>

      <h2 className="font-['Inter'] text-[42px] font-semibold text-[#393939] mb-5">
        Upload Sheet
      </h2>

      <div className="w-[1000px] h-[280px] bg-[#fdeded] border-2 border-dashed border-[#b4b3b3] rounded-md flex flex-col items-center justify-center relative mb-5">
        <p className="text-base text-[#666] mt-4.5">
          Drag & drop files or{" "}
          <span
            className="text-[#f34a4d] underline cursor-pointer"
            onClick={handleBrowseClick}
          >
            Browse
          </span>
        </p>

        {fileName && <p className="text-sm text-[#333] mt-2">{fileName}</p>}

        <p className="text-xs text-[#999]">Supported formats: xlsx & csv</p>

        <input
          type="file"
          accept=".xlsx,.csv"
          onChange={handleFileUpload}
          className="upload-input hidden"
          ref={fileInputRef}
        />

        <button
          type="button"
          className="flex items-center bg-[#c12328] text-white border-none px-5 py-2.5 text-base font-medium rounded-lg cursor-pointer shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] hover:bg-[#f34a4d] mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSubmit}
          disabled={isLoading || !file}
        >
          <CloudUpload className="mr-2" />
          {isLoading ? "Uploading..." : "Upload File"}
        </button>
      </div>
    </div>
  );
};

export default FileUpload;